/**
 * supabase/messages.js — Mensajes y conversaciones: lectura unificada
 * (v_mensajes_unificados), persistencia, envío por WhatsApp
 * (sendLeadMessage), edición/borrado y realtime (subscribeToMessages).
 * Parte de la fachada src/lib/supabase.js.
 */

import { sendWhatsAppMessage } from "../n8n-api";
import { getSupabase, supabase } from "./client";

// ═══════════════════════════════════════════════════
// MESSAGES — Usa VIEW v_mensajes_unificados
// ═══════════════════════════════════════════════════

/**
 * Obtiene mensajes de un lead usando la VIEW unificada.
 *
 * ANTES: 2 queries (messages + n8n_chat_histories) + merge/dedup en JS
 * AHORA: 1 query a v_mensajes_unificados
 *
 * @param {Object} params
 * @param {string|null} params.wa_jid  - WhatsApp JID
 * @param {string|null} [params.leadId] - UUID del lead
 * @param {number}      [params.limit]  - Max mensajes (default 500)
 * @returns {Promise<Array>}
 */
export async function getLeadMessages({
  wa_jid: waJid,
  leadId = null,
  limit = 500,
}) {
  const client = getSupabase();

  // Resolver wa_jid desde el lead si no viene
  if (!waJid && leadId) {
    const { data: lead } = await client
      .from("leads")
      .select("wa_jid, telefono")
      .eq("id", leadId)
      .maybeSingle();

    waJid =
      lead?.wa_jid ||
      (lead?.telefono ? `${lead.telefono}@s.whatsapp.net` : null);
  }

  if (!waJid && !leadId) return [];

  // Query a la VIEW unificada
  // nullsFirst:false → mensajes sin timestamp se quedan al final (no brincan arriba)
  let query = client
    .from("v_mensajes_unificados")
    .select("*")
    .order("created_at", { ascending: true, nullsFirst: false })
    .limit(limit);

  // Preferir lead_id estricto cuando esté disponible. El fallback a wa_jid
  // captura mensajes sin lead_id aún resuelto (trigger pendiente o legacy),
  // SIN traer mensajes de otros leads que casualmente compartan wa_jid.
  if (leadId && waJid) {
    query = query.or(`lead_id.eq.${leadId},and(lead_id.is.null,wa_jid.eq.${waJid})`);
  } else if (leadId) {
    query = query.eq("lead_id", leadId);
  } else if (waJid) {
    query = query.eq("wa_jid", waJid);
  }

  const { data, error } = await query;

  if (error) {
    console.warn("[supabase] getLeadMessages error:", error.message);
    return [];
  }

  // Mapear actor → tipo para compat con el frontend
  return (data ?? []).map((msg) => ({
    id: msg.id,
    mensaje: msg.mensaje ?? "",
    tipo:
      msg.actor === "asesor"
        ? "agent"
        : msg.actor === "bot"
          ? "bot"
          : "cliente",
    wa_jid: msg.wa_jid,
    sesion_id: msg.wa_jid,
    created_at: msg.created_at,
    source: msg.source,
    direction: msg.direction,
    wa_message_id: msg.wa_message_id ?? null,
    deleted_at: msg.deleted_at ?? null,
    edited_at: msg.edited_at ?? null,
  }));
}

// ═══════════════════════════════════════════════════
// CONVERSATIONS
// ═══════════════════════════════════════════════════

/**
 * Guarda un mensaje en la tabla messages.
 * @param {Object} params
 * @param {string|null} params.leadId
 * @param {string|null} params.waJid
 * @param {string} params.senderType - "lead" | "bot" | "advisor" | "agent"
 * @param {string} params.body
 * @returns {Promise<Object>} mensaje insertado
 */
export async function saveConversationMessage({
  leadId,
  waJid = null,
  senderType,
  body,
  waMessageId = null,
}) {
  if (!senderType || !body) throw new Error("senderType y body son requeridos");

  const client = getSupabase();

  // Resolver wa_jid
  if (!waJid && leadId) {
    const { data } = await client
      .from("leads")
      .select("wa_jid, telefono")
      .eq("id", leadId)
      .maybeSingle();
    waJid =
      data?.wa_jid ||
      (data?.telefono ? `${data.telefono}@s.whatsapp.net` : null);
  }

  if (!waJid) throw new Error("wa_jid requerido para guardar mensaje");

  // Mapear senderType → actor (CHECK constraint: lead/bot/asesor)
  const actor =
    senderType === "bot"
      ? "bot"
      : ["advisor", "agent", "respuesta", "asesor"].includes(senderType)
        ? "asesor"
        : "lead";

  const payload = {
    lead_id: leadId ?? null,
    wa_jid: waJid,
    body: body.trim(),
    actor,
    direction: actor === "lead" ? "in" : "out",
  };
  if (waMessageId) payload.wa_message_id = waMessageId;

  const { data, error } = await client
    .from("messages")
    .insert(payload)
    .select()
    .single();

  if (error) {
    // 23505 = unique_violation: ya existe un mensaje con ese wa_message_id
    // (ej. Evolution disparó el webhook con el mismo id). No es error real.
    if (error.code === "23505") {
      console.info("[messages] duplicado evitado por wa_message_id");
      return null;
    }
    throw error;
  }
  return data;
}

// ═══════════════════════════════════════════════════
// SEND MESSAGE (WhatsApp + persistencia)
// ═══════════════════════════════════════════════════

/**
 * Envía un mensaje: guarda en DB + envía por WhatsApp si es modo humano.
 *
 * @param {Object} params
 * @param {string|null} params.wa_jid
 * @param {string} params.mensaje
 * @param {string} [params.tipo="agent"]
 * @param {string|null} [params.leadId]
 * @param {string|null} [params.telefono]
 * @param {boolean} [params.isHumanMode=false]
 * @param {string|null} [params.asesor_email]
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function sendLeadMessage({
  wa_jid: waJid,
  mensaje,
  tipo = "agent",
  leadId = null,
  telefono = null,
  isHumanMode = false,
  asesor_email = null,
  sesion_id: _sesionId = null,
  instancia = null,
}) {
  const client = getSupabase();

  if (!mensaje?.trim())
    return { data: null, error: new Error("Mensaje vacío") };

  // ── Resolver wa_jid, lead e instancia ──
  let resolvedInstancia = instancia;
  if (!waJid && leadId) {
    const { data: lead } = await client
      .from("leads")
      .select("wa_jid, telefono, instancia")
      .eq("id", leadId)
      .maybeSingle();
    waJid =
      lead?.wa_jid ||
      (lead?.telefono ? `${lead.telefono}@s.whatsapp.net` : null);
    telefono = telefono || lead?.telefono;
    resolvedInstancia = resolvedInstancia || lead?.instancia;
  } else if (!waJid && telefono) {
    waJid = `${telefono}@s.whatsapp.net`;
  }

  if (!waJid) return { data: null, error: new Error("wa_jid requerido") };

  // Resolver leadId si no viene
  let resolvedLeadId = leadId;
  if (!resolvedLeadId) {
    const { data: lead } = await client
      .from("leads")
      .select("id, telefono, instancia")
      .eq("wa_jid", waJid)
      .maybeSingle();
    resolvedLeadId = lead?.id ?? null;
    telefono = telefono || lead?.telefono;
    resolvedInstancia = resolvedInstancia || lead?.instancia;
  }

  try {
    // 1. Guardar en messages
    const saved = await saveConversationMessage({
      leadId: resolvedLeadId,
      waJid,
      senderType: tipo === "agent" ? "agent" : tipo,
      body: mensaje.trim(),
    });

    // 2. Guardar en n8n_chat_histories solo si NO es intervención humana.
    //    En modo humano el bot está pausado y la VIEW v_mensajes_unificados
    //    interpreta type:"ai" como actor=bot, lo que haría aparecer el mensaje
    //    del asesor como "BOT IA" duplicado.
    if (!isHumanMode) {
      try {
        const sessionId = waJid;
        await client.from("n8n_chat_histories").insert({
          session_id: sessionId,
          message: {
            type: "ai",
            content: mensaje.trim(),
            additional_kwargs: { asesor_email, is_human_response: false },
          },
        });
      } catch (e) {
        console.warn("[supabase] n8n_chat_histories insert failed (non-blocking):", e.message);
      }
    }

    // 3. Enviar por WhatsApp si es modo humano.
    //    Al terminar, guardar el key.id de Evolution en el mensaje para evitar
    //    duplicados cuando el webhook SEND_MESSAGE dispare el mismo mensaje.
    if (isHumanMode && telefono) {
      const sendRes = await sendWhatsAppMessage({
        phone: telefono,
        message: mensaje.trim(),
        leadId: resolvedLeadId,
        wa_jid: waJid,
        instance: resolvedInstancia,
      });
      const waMessageId = sendRes?.data?.key?.id;
      if (waMessageId && saved?.id) {
        try {
          await client
            .from("messages")
            .update({ wa_message_id: waMessageId })
            .eq("id", saved.id);
        } catch (e) {
          console.warn("[supabase] no se pudo guardar wa_message_id:", e.message);
        }
      }
    }

    // 4. Touch última interacción (trigger pone updated_at)
    if (resolvedLeadId) {
      await client
        .from("leads")
        .update({ ultima_interaccion: new Date().toISOString() })
        .eq("id", resolvedLeadId);
    }

    return {
      data: {
        id: saved?.id,
        mensaje: mensaje.trim(),
        tipo: "agent",
        wa_jid: waJid,
        created_at: saved?.created_at || new Date().toISOString(),
      },
      error: null,
    };
  } catch (err) {
    console.warn("[supabase] sendLeadMessage error:", err.message);
    return { data: null, error: err };
  }
}

/**
 * Edita el body de un mensaje y marca edited_at = now().
 */
export async function editLeadMessage(messageId, newBody) {
  if (!messageId) throw new Error("messageId requerido");
  if (!newBody?.trim()) throw new Error("Texto vacío");
  const client = getSupabase();
  const { data, error } = await client
    .from("messages")
    .update({ body: newBody.trim(), edited_at: new Date().toISOString() })
    .eq("id", messageId)
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

/**
 * Soft-delete de un mensaje. Marca deleted_at = now() en la tabla messages.
 * El frontend filtra/renderiza el placeholder "Mensaje eliminado".
 */
export async function deleteLeadMessage(messageId) {
  if (!messageId) throw new Error("messageId requerido");
  const client = getSupabase();
  const { data, error } = await client
    .from("messages")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", messageId)
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

// ═══════════════════════════════════════════════════
// REALTIME — Cada caller tiene su propio channel (no singleton)
// ═══════════════════════════════════════════════════

/**
 * Suscribe a mensajes de UN solo lead (el chat abierto).
 * Cada llamada crea un channel propio que el caller debe limpiar
 * con `supabase.removeChannel(channel)` al desmontar.
 *
 * Filtra por lead_id Y wa_jid (fallback) para mensajes nuevos del lead
 * que aún no tengan lead_id resuelto en BD (ocurre si el insert llega
 * antes que el lookup del lead_id).
 *
 * @param {string} leadId - UUID del lead
 * @param {Function} callback - Recibe el mensaje nuevo normalizado
 * @returns {Promise<import("@supabase/supabase-js").RealtimeChannel|null>}
 */
export async function subscribeToMessages(leadId, callback) {
  if (!leadId) return null;

  // Buscar wa_jid para fallback (mensajes que llegan sin lead_id resuelto aún)
  let waJid = null;
  try {
    const { data } = await supabase
      .from("leads")
      .select("wa_jid")
      .eq("id", leadId)
      .maybeSingle();
    waJid = data?.wa_jid || null;
  } catch {
    /* ignore */
  }

  const normalize = (m) => ({
    id: m.id,
    mensaje: m.body ?? "",
    tipo:
      m.actor === "asesor"
        ? "agent"
        : m.actor === "bot"
          ? "bot"
          : "cliente",
    wa_jid: m.wa_jid,
    sesion_id: m.wa_jid,
    created_at: m.created_at,
    direction: m.direction,
  });

  // Channel único por suscripción (timestamp evita colisiones entre re-renders)
  const channelName = `msg:${leadId}:${Date.now()}`;
  const channel = supabase.channel(channelName);

  // Listener primario: filtro por lead_id
  channel.on(
    "postgres_changes",
    {
      event: "INSERT",
      schema: "public",
      table: "messages",
      filter: `lead_id=eq.${leadId}`,
    },
    (payload) => {
      if (payload?.new) callback(normalize(payload.new));
    },
  );

  // Listener fallback: si llega un INSERT con el mismo wa_jid pero lead_id null
  // (caso típico: el bot guardó el msg antes de resolver lead_id),
  // también lo entregamos.
  if (waJid) {
    channel.on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "messages",
        filter: `wa_jid=eq.${waJid}`,
      },
      (payload) => {
        const m = payload?.new;
        if (!m) return;
        // Evitar duplicado: si ya tiene lead_id que matchea, el listener primario lo atrapa
        if (m.lead_id && m.lead_id === leadId) return;
        callback(normalize(m));
      },
    );
  }

  channel.subscribe((status, err) => {
    if (status === "SUBSCRIBED") {
      console.log(`[realtime] suscrito a ${channelName} (wa_jid=${waJid})`);
    } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
      console.warn(`[realtime] estado=${status}`, err);
    }
  });

  return channel;
}
